import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from "react";
import { z } from "zod";
import { Form } from "@unform/web";
import { useSelector } from "react-redux";
import { useToast } from "../../hooks/toast";
import { useHistory, useParams } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { validateSingleFieldOnBlur } from "../../utils/zodFormValidations";
import getValidationErrors from "../../utils/getZodValidationErrors";
import api from "../../service";
import AnimatedMainContainer from "../../components/AnimatedMainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import InputWithLabel from "../../components/InputWithLabel";
import Table from "../../components/Table";
import FormButton from "../../components/FormButton";
import Badge from "../../components/Badge";
import {
  Container,
  FormContainer,
  LinkButton,
  Title,
  TitleContainer,
  BadgeContainer,
  ActionContainer,
  Horizontal,
  FieldsContainer,
  SubmitContainer,
} from "./styles";
import DuplicateChecklistModal from "./DuplicateChecklistModal";

const Checklist = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [checkList, setChecklist] = useState([]);
  const [deleteId, setDeleteId] = useState(0);
  const formRef = useRef(null);
  const intl = useIntl();
  const { token } = useSelector((state) => state.auth);
  const { addToast } = useToast();
  const { id } = useParams();
  const history = useHistory();
  const [fetchDataForm, setFetchDataForm] = useState({});
  const [duplicateChecklistModalIsOpen, setDuplicateChecklistModalIsOpen] =
    useState(false);
  const [showDuplicateButton, setShowDuplicateButton] = useState(false);
  const defaultFetchDataForm = useMemo(() => {
    return {
      eventId: id,
      descriptionName: ""
    }
  }, [id]);

  const schema = z.object({
    checklistName: z.string().optional(),
  });

  const showConfirmation = (id) => {
    setModalIsOpen(true);
    setDeleteId(id);
  };

  const fetchData = useCallback(
    async (requestBody) => {
      setIsLoading(true);
      if (isLoading) return;

      api
        .patch(
          `/event-checklist`,
          requestBody,
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((result) => {
          setChecklist(result.data.output);
          if (
            result.data.output.length === 0 &&
            requestBody.descriptionName === ""
          ) {
            setShowDuplicateButton(true);
          }
        })
        .catch((err) => {
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, [addToast, intl, token]
  );

  useEffect(() => {
    setFetchDataForm(defaultFetchDataForm);
    fetchData(defaultFetchDataForm);
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        schema.parse(data);

        const formData = {
          eventId: id,
          descriptionName: data.checklistName,
        };

        setFetchDataForm(formData);
        fetchData(formData);
      } catch (err) {
        if (err.errors) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }
      }
    },
    [isLoading, schema, fetchData]
  );

  const clearFields = () => {
    formRef.current.reset();
    setFetchDataForm(defaultFetchDataForm);
    fetchData(defaultFetchDataForm);
  };

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();

    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  const handleDeleteItem = () => {
    setIsLoading(true);
    if (isLoading) {
      return;
    }
    api
      .delete(`/event-checklist/${deleteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        setIsLoading(false);
        addToast({
          type: "success",
          title: intl.formatMessage({
            id: "success",
          }),
          description: intl.formatMessage({
            id: "success.faq_delete",
          }),
        });
        const dataRemaining = checkList.filter((d) => d.idNumber !== deleteId);

        setChecklist(dataRemaining);
        setModalIsOpen(false);
      })
      .catch((err) => {
        setIsLoading(false);
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
        setModalIsOpen(false);
      });
    setDeleteId(0);
  };

  const columns = [
    {
      name: intl.formatMessage({ id: "checklist.abrev.item_description_pt" }),
      selector: (row) => row.descriptionPt,
      sortable: true,
      maxWidth: "250px",
    },
    {
      name: intl.formatMessage({ id: "checklist.abrev.item_description_en" }),
      selector: (row) => row.descriptionEn || "-",
      sortable: true,
      maxWidth: "250px",
    },
    {
      name: "",
      grow: 1,
    },
    {
      cell: (row) => (
        <LinkButton to={`/event-checklist/${id}/item/edit/${row.idNumber}`}>
          <Badge color="#002244" text={intl.formatMessage({ id: "edit" })} />
        </LinkButton>
      ),
      right: true,
      grow: 0,
      maxWidth: "140px",
      minWidth: "140px",
    },
    {
      cell: (row) => (
        <Badge
          color="#cc0000"
          text={intl.formatMessage({ id: "delete" })}
          onClick={() => showConfirmation(row.idNumber)}
        />
      ),
      right: true,
      grow: 0,
      maxWidth: "140px",
      minWidth: "140px",
    },
  ];

  const pushBack = () => {
    history.push("/events");
  };

  const handleOpenDuplicateChecklistModal = () => {
    setDuplicateChecklistModalIsOpen(true);
  };

  const callbackDuplicateChecklist = () => {
    setShowDuplicateButton(false);
    fetchData(fetchDataForm);
  };

  return (
    <>
      <AnimatedMainContainer breadcrumb="Checklist">
        <Form ref={formRef} onSubmit={handleSubmit}>
          <Container>
            <FormContainer>
              <TitleContainer>
                <Title>
                  <FormattedMessage id="checklist.edit" />
                </Title>
                <ActionContainer>
                  {showDuplicateButton && (
                    <BadgeContainer onClick={() => handleOpenDuplicateChecklistModal()}>
                      <Badge
                        color="#33D27E"
                        text={intl.formatMessage({ id: "duplicate_checklist" })}
                        fontSize="12px"
                      />
                    </BadgeContainer>
                  )}
                  <BadgeContainer onClick={pushBack}>
                    <Badge
                      color="#C9C9C9"
                      text={intl.formatMessage({ id: "goback" })}
                      fontSize="12px"
                    />
                  </BadgeContainer>
                  <BadgeContainer
                    onClick={() =>
                      history.push(`/event-checklist/${id}/item/create`)
                    }
                  >
                    <Badge
                      color="#002244"
                      text={intl.formatMessage({ id: "new" })}
                      fontSize="12px"
                    />
                  </BadgeContainer>
                </ActionContainer>
              </TitleContainer>
              <FieldsContainer>
                <Horizontal>
                  <InputWithLabel
                    name="checklistName"
                    validationBlur={validationBlur}
                    label={intl.formatMessage({ id: "item" })}
                  />
                  <SubmitContainer>
                    <FormButton
                      color="#C9C9C9"
                      type="button"
                      onClick={clearFields}
                    >
                      <FormattedMessage id="clear_fields" />
                    </FormButton>
                  </SubmitContainer>
                  <SubmitContainer>
                    <FormButton type="submit" loading={isLoading}>
                      <FormattedMessage id="search" />
                    </FormButton>
                  </SubmitContainer>
                </Horizontal>
              </FieldsContainer>
            </FormContainer>
          </Container>
        </Form>
        <Table
          columns={columns}
          data={checkList}
          title={intl.formatMessage({ id: "results" })}
        />
      </AnimatedMainContainer>
      <ConfirmationModal
        setIsOpen={setModalIsOpen}
        modalIsOpen={modalIsOpen}
        confirmationFunction={() => handleDeleteItem()}
        description={intl.formatMessage({ id: "exclusion_confirmation" })}
        confirmationText={intl.formatMessage({ id: "yes" })}
        cancelText={intl.formatMessage({ id: "no" })}
        isLoading={isLoading}
      />
      <DuplicateChecklistModal
        setOpen={setDuplicateChecklistModalIsOpen}
        open={duplicateChecklistModalIsOpen}
        targetEventId={id}
        updateCallback={() => callbackDuplicateChecklist()}
      />
    </>
  );
};

export default Checklist;
